"use client";

import { useState, type InputHTMLAttributes } from "react";
import { cx } from "../cx.ts";
import { IconButton } from "../ui/icon-button.tsx";

type Size = "md" | "lg";

/**
 * `lg` is the till, and all three values move together on purpose.
 *
 * The height is the obvious one — a terminal is typed on with a thumb. The text
 * step goes with it because the till's other inputs sit at the base size, and
 * one field a step smaller than the one above it reads as a mistake. The button
 * goes with it because a 32px target under a finger is the actual defect a
 * taller field would otherwise hide, and the padding has to clear whichever
 * button it gets.
 */
const SIZES: Record<Size, { field: string; button: "sm" | "md" }> = {
  md: { field: "h-10 pe-11 text-sm", button: "sm" },
  lg: { field: "h-12 pe-12 text-base", button: "md" },
};

// `size` is dropped along with `type`: the HTML attribute is a character-width
// nothing here sets, and leaving it would intersect with the prop below to
// `never` rather than overriding it.
export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  invalid?: boolean;
  /**
   * The reveal control's accessible name, and its only one. A *prop* rather
   * than a bare `aria-label` for the reason `IconButton` gives: a hardcoded
   * English string at an app call site is then caught by
   * `untranslated-strings.test.ts`. App callers pass `t(...)`.
   *
   * It does not change with the state — `aria-pressed` carries that, which is
   * what a toggle button is for.
   */
  label: string;
  size?: Size;
};

/**
 * A password field that can show what was typed.
 *
 * It owns an `<input>` rather than wrapping `TextInput`, and the deciding case
 * is the till: `h-12` layered over `TextInput`'s `h-10` is the exact trap
 * `select.tsx` documents — `cx` concatenates and does not merge, both classes
 * land, and the stylesheet's order rather than the caller picks the winner.
 * Owning the class list makes the height a value, not a collision. `className`
 * therefore lands on the wrapper, which is `SearchInput`'s convention for any
 * input that has one.
 *
 * The reveal flips `type` on the same node rather than swapping in a second
 * input: the caret, the value and the password manager's grip on the field all
 * survive that, and none of them survive a swap.
 *
 * `revealed` is local and stays local. Nothing outside needs to know, and a
 * reveal that survived being re-parented would be a leak rather than a feature.
 */
export function PasswordInput({
  className,
  invalid,
  label,
  size = "md",
  ...rest
}: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);
  const { field, button } = SIZES[size];

  return (
    <div className={cx("relative flex w-full items-center", className)}>
      <input
        // Flipping this does not disturb a password manager: fill keys off
        // `autoComplete` and the field name, which the caller still owns.
        type={revealed ? "text" : "password"}
        // A revealed password is not prose. Without these, a desktop browser
        // underlines it in red and an Android soft keyboard capitalises the
        // first letter — a rejected login whose cause is invisible.
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        className={cx(
          // The padding that clears the button is logical, so it moves to the
          // left in a right-to-left locale without a second rule.
          // `::-ms-reveal` is Edge's own eye, which would otherwise sit beside
          // ours — two toggles a few pixels apart is worse than none.
          "w-full rounded-control border bg-surface ps-3 text-ink-body [&::-ms-reveal]:hidden",
          "placeholder:text-ink-subtle transition-colors duration-150 focus:border-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
          "disabled:cursor-not-allowed disabled:opacity-50",
          field,
          invalid ? "border-danger" : "border-line hover:border-line-strong",
        )}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {/* Absolutely positioned and still vertically centred: the wrapper's
          `items-center` governs an out-of-flow child whose block-axis insets
          are auto, which is why this holds at both heights. `SearchInput`
          leans on the same rule. */}
      <IconButton
        icon={revealed ? "eye-off" : "eye"}
        label={label}
        size={button}
        aria-pressed={revealed}
        onClick={() => setRevealed((current) => !current)}
        className="absolute end-1"
      />
    </div>
  );
}
